import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { rtdb } from "utils/firebase";
import { environment } from "utils/constant";

const NotificationContext = createContext();

const getStoredOrgId = () => {
  try {
    const org = JSON.parse(localStorage.getItem("organization") || "null");
    return org?.id ?? null;
  } catch (e) {
    return null;
  }
};

const getStoredUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return user?.user ?? user?.id ?? user?.user_id ?? user?.pk ?? null;
  } catch (e) {
    return null;
  }
};

const getNotificationPreferenceKey = (userId) =>
  userId ? `${userId}-notifications` : null;

const getStoredNotificationPreference = (userId) => {
  const key = getNotificationPreferenceKey(userId);
  if (!key) return true;

  try {
    const storedValue = localStorage.getItem(key);
    return storedValue === null ? true : storedValue === "true";
  } catch (e) {
    return true;
  }
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children, organizationId: propOrganizationId, userId: propUserId }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newNotification, setNewNotification] = useState(null);
  const [organizationId, setOrganizationId] = useState(
    () => propOrganizationId ?? getStoredOrgId()
  );
  const [userId, setUserId] = useState(() => propUserId ?? getStoredUserId());
  const [notificationsEnabled, setNotificationsEnabledState] = useState(() =>
    getStoredNotificationPreference(propUserId ?? getStoredUserId())
  );
  const notificationsEnabledRef = useRef(notificationsEnabled);

  // Keep in sync with props
  useEffect(() => {
    const nextOrganizationId = propOrganizationId ?? getStoredOrgId();
    setOrganizationId((currentOrganizationId) =>
      currentOrganizationId === nextOrganizationId
        ? currentOrganizationId
        : nextOrganizationId
    );
  }, [propOrganizationId]);

  useEffect(() => {
    const nextUserId = propUserId ?? getStoredUserId();
    setUserId((currentUserId) =>
      currentUserId === nextUserId ? currentUserId : nextUserId
    );
  }, [propUserId]);

  useEffect(() => {
    setNotificationsEnabledState(getStoredNotificationPreference(userId));
  }, [userId]);

  // Listen for changes in localStorage
  useEffect(() => {
    const syncFromStorage = () => {
      const nextOrganizationId = getStoredOrgId();
      const nextUserId = getStoredUserId();

      setOrganizationId((currentOrganizationId) =>
        currentOrganizationId === nextOrganizationId
          ? currentOrganizationId
          : nextOrganizationId
      );
      setUserId((currentUserId) =>
        currentUserId === nextUserId ? currentUserId : nextUserId
      );
    };

    const handleStorageChange = (e) => {
      if (!e.key || e.key === "organization" || e.key === "user") {
        syncFromStorage();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("organizationChanged", syncFromStorage);
    window.addEventListener("authChanged", syncFromStorage);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("organizationChanged", syncFromStorage);
      window.removeEventListener("authChanged", syncFromStorage);
    };
  }, []);

  useEffect(() => {
    const syncNotificationPreference = () => {
      setNotificationsEnabledState(getStoredNotificationPreference(userId));
    };

    const handleStorageChange = (e) => {
      if (!userId || e.key === getNotificationPreferenceKey(userId)) {
        syncNotificationPreference();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(
      "notificationPreferenceChanged",
      syncNotificationPreference
    );

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "notificationPreferenceChanged",
        syncNotificationPreference
      );
    };
  }, [userId]);

  useEffect(() => {
    if (!notificationsEnabled) {
      setNewNotification(null);
    }
    notificationsEnabledRef.current = notificationsEnabled;
  }, [notificationsEnabled]);

  const showBrowserNotification = useCallback(
    (notification) => {
      if (!notificationsEnabledRef.current) return;

      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(notification.title, {
          body: notification.message,
          icon: "/download.svg",
          tag: notification.id, // Prevents duplicate notifications
        });
      }
    },
    []
  );

  useEffect(() => {
    setNotifications([]);
    setUnreadCount(0);
    setNewNotification(null);
    updateTabTitle(0);
  }, [organizationId, userId]);

  // Listen for real-time notifications
  useEffect(() => {
    if (!organizationId || !userId) return;

    const notificationsRef = rtdb.ref(`notifications-${environment}/${organizationId}`);

    const handleNotificationChange = (snapshot) => {
      const notificationsData = snapshot.val() || {};
      const notificationsList = Object.entries(notificationsData)
        .map(([key, value]) => ({ id: key, ...value }))
        .filter((n) => n.target_ids && n.target_ids.includes(userId) && n.actor_id !== userId)
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      setNotifications(notificationsList);

      const unread = notificationsList.filter((n) => !(n.read_by || []).includes(userId)).length;
      setUnreadCount(unread);

      // Update browser tab title
      updateTabTitle(unread);
    };

    // Listen for new notifications
    const handleChildAdded = (snapshot) => {
      const notification = snapshot.val();
      if (notification) {
        const notificationWithId = { id: snapshot.key, ...notification };
        const actor_id = notification.actor_id || null;
        const target_ids = notification.target_ids || [];
        const isTarget = target_ids.includes(userId);
        const isNotActor = actor_id !== userId;
        const isUnread = !(notification.read_by || []).includes(userId);

        if (
          isTarget &&
          isNotActor &&
          isUnread &&
          notificationsEnabledRef.current
        ) {
          setNewNotification(notificationWithId);

          // Show browser notification if permission granted
          showBrowserNotification(notificationWithId);

          // Play notification sound
          // playNotificationSound();
        }
      }
    };

    notificationsRef.on("value", handleNotificationChange);

    notificationsRef.on("child_added", handleChildAdded);

    return () => {
      notificationsRef.off("value", handleNotificationChange);
      notificationsRef.off("child_added", handleChildAdded);
    };
  }, [organizationId, userId, showBrowserNotification]); // Added userId to dependencies

  const updateTabTitle = (count) => {
    const baseTitle = document.title.replace(/^\(\d+\)\s*/, "");
    document.title = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;

    // Update favicon with badge
    // updateFavicon(count);
  };

  const updateFavicon = (count) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    canvas.width = 32;
    canvas.height = 32;

    img.onload = () => {
      // Draw the original favicon
      ctx.drawImage(img, 0, 0, 32, 32);

      if (count > 0) {
        // Draw notification badge with larger size
        ctx.fillStyle = "#FF0000"; // Brighter red
        ctx.beginPath();
        ctx.arc(24, 8, 8, 0, 2 * Math.PI); // Increased radius from 8 to 10
        ctx.fill();

        // Add a white border for better visibility
        ctx.strokeStyle = "white";
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw count text with improved visibility
        ctx.fillStyle = "white";
        ctx.font = "bold 11px Arial"; // Increased font size
        ctx.textAlign = "center";
        ctx.textBaseline = "middle"; // Better text alignment
        ctx.fillText(count > 99 ? "99+" : count.toString(), 24, 8);
      }

      // Update favicon
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = canvas.toDataURL();
    };

    // Load current favicon or use default
    img.src =
      document.querySelector("link[rel*='icon']")?.href || "/download.svg";
  };

  useEffect(() => {
    const unread = notifications.filter((n) => !(n.read_by || []).includes(userId)).length;
    setUnreadCount(unread);
    updateTabTitle(unread);
  }, [notifications, userId]);

  const playNotificationSound = () => {
    // You can add a notification sound file to your public folder
    const audio = new Audio("/web-notification.wav");
    audio.volume = 0.3;
    audio.play().catch(() => {
      // Ignore errors if sound can't play
    });
  };

  const markAsRead = (notificationId) => {
    if (!organizationId || !userId) return;
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification) return;

    const readBy = notification.read_by || [];
    if (!readBy.includes(userId)) {
      const notificationsRef = rtdb.ref(
        `notifications-${environment}/${organizationId}`
      );
      notificationsRef.child(notificationId).update({
        read_by: [...readBy, userId]
      });
    }
  };

  const markAllAsRead = () => {
    if (!organizationId || !userId) return;
    const notificationsRef = rtdb.ref(
      `notifications-${environment}/${organizationId}`
    );
    notifications
      .filter((n) => !(n.read_by || []).includes(userId))
      .forEach((notification) => {
        const readBy = notification.read_by || [];
        notificationsRef.child(notification.id).update({
          read_by: [...readBy, userId]
        });
      });
  };

  const dismissNewNotification = () => {
    setNewNotification(null);
  };

  const setNotificationsEnabled = (enabled) => {
    setNotificationsEnabledState(enabled);

    const key = getNotificationPreferenceKey(userId);
    if (!key) return;

    localStorage.setItem(key, String(enabled));
    window.dispatchEvent(new Event("notificationPreferenceChanged"));

    if (
      enabled &&
      "Notification" in window &&
      Notification.permission === "default"
    ) {
      Notification.requestPermission();
    }
  };

  const requestNotificationPermission = async () => {
    if (!notificationsEnabled) return false;
    if (!("Notification" in window)) return false;

    if ("Notification" in window && Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }
    return Notification.permission === "granted";
  };

  const value = {
    notifications,
    notificationsEnabled,
    unreadCount,
    newNotification,
    markAsRead,
    markAllAsRead,
    dismissNewNotification,
    setNotificationsEnabled,
    requestNotificationPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
